/**
 * WHICH RANKS BELONG TO THE VISIBLE CAREER LADDER — AND WHOSE LADDER IT IS.
 *
 * The ladder the reset switches on ends like this:
 *
 *   … → CROWN (1100) → CORONA-REAL (1200) → BLACK-CROWN (1300)
 *       → COFOUNDER (2000) → UNKNOWN (999999)
 *
 * and both COFOUNDER and UNKNOWN come out of paso 3 as `status: "active"`. They are
 * NOT the same kind of thing, and neither is a plain rank. Hence THREE levels — the
 * whole point of this module:
 *
 *  1. **SENTINEL** (`UNKNOWN`) — never visible, to ANYBODY, not even to somebody the
 *     data claims holds it. It is never awarded: it exists only so that "the rank
 *     above the top one" resolves to something instead of falling off the end of the
 *     ladder. Showing it as a goal is the bug this module was written for — its
 *     display name even carries a typo ("Unkown Secret").
 *
 *  2. **RESTRICTED** (`COFOUNDER`) — visible ONLY to the person who holds it. It is a
 *     real rank that real people (the founders) hold, so a cofounder sees it on their
 *     own ladder; but it is not a goal anybody climbs towards, so for everyone else
 *     the ladder is CUT at BLACK-CROWN and there is no next rank above it.
 *     (Owner's call, 2026-07-22: «ese rango SOLO debe de aparecerle a cofounders,
 *     los demás el más alto es el BLACK-CROWN y aquí se debe de cortar la lista».)
 *
 *  3. **NORMAL** — everything else: always visible.
 *
 * ── Why level 1 lives in CODE and level 2 lives in DATA ──────────────────────────
 *
 * They fail in opposite directions, so they are stored in opposite places.
 *
 *  - The sentinel rule must fail CLOSED. If it depended on a payload field and that
 *    field were ever dropped, renamed or mistyped, a rank with priority 999999 would
 *    silently go back to being the last rung of EVERYBODY's ladder — the exact bug.
 *    So the sentinel list is an id list in code: one id, right here, tested. If a
 *    second sentinel is ever minted, it is added here — a one-line change, in a file
 *    whose only job is to say what a sentinel is.
 *
 *  - The restricted rule may fail OPEN. It is driven by the payload's
 *    `hiddenInProgress` flag, whose name says exactly this level's meaning: "hidden
 *    while it is only a goal". If the flag were ever dropped, COFOUNDER would become
 *    visible to everybody again — which is where the product stood before this
 *    change: a cosmetic regression, not a correctness one. That is affordable, and it
 *    is what buys the operator the ability to mark a future rank restricted by
 *    editing the ranks configuration instead of shipping code.
 *
 * `documentation/implementations/2026-07-18-ranksConfiguration-payload.json` carries
 * `hiddenInProgress: true` on BOTH ranks, and that is CORRECT under this design: on
 * COFOUNDER it is the flag being read here, and on UNKNOWN it is redundant but
 * harmless — level 1 is checked first and wins, so the sentinel stays invisible even
 * to a viewer who somehow "held" it. (UNKNOWN additionally carries `isSentinel: true`
 * in that payload; this module deliberately does NOT read it, for the fail-closed
 * reason above.)
 *
 * ── How a caller answers "whose ladder is this?" ─────────────────────────────────
 *
 * Every entry point takes a {@link RankViewer}: the id of the rank that person holds,
 * plus a lookup into the ranks catalogue to read `hiddenInProgress` from. Both fields
 * are optional because "not loaded yet" is a real state, and both degrade the way the
 * levels above say they should: no `rankById` ⇒ nothing looks restricted ⇒ level 2
 * is not enforced (fails open); no `heldRankId` ⇒ nobody holds anything ⇒ restricted
 * ranks are hidden (fails closed). The sentinel rule needs neither and never yields.
 *
 * Deliberately NOT here: `inactive` / `unused` / `deleted` ranks. Those never reach
 * the ladder in the first place — every reader queries `status == "active"`.
 */

/**
 * Rank ids that exist only as end-of-ladder markers and are never awarded.
 * A sentinel is never shown, to anybody, under any status.
 */
export const SENTINEL_RANK_IDS: ReadonlySet<string> = new Set<string>([
  "UNKNOWN",
]);

/**
 * The only part of a rank definition this module reads.
 *
 * `_id` is not used by the rule; it is declared so that passing a full `Rank` (which
 * does not necessarily declare `hiddenInProgress` in its own type) is not rejected by
 * TypeScript's weak-type check.
 */
export interface RankVisibilityFields {
  readonly _id?: string;
  readonly hiddenInProgress?: boolean;
}

/**
 * Resolves a rank id to its definition — i.e. the `ranks` catalogue.
 * Every surface already has one: `rankById` in the ranks store.
 */
export type RankResolver = (
  rankId: string
) => RankVisibilityFields | null | undefined;

/**
 * Whose ladder is being drawn, and how to look a rank up.
 *
 * @property heldRankId - The rank this person currently holds (`currentRank`, or the
 *   `ranksCarrerData` entry with status `"current"`). Restricted ranks are shown only
 *   to the holder.
 * @property rankById - The ranks catalogue lookup, the only source of
 *   `hiddenInProgress`. Omitted / not loaded ⇒ no rank is restricted.
 */
export interface RankViewer {
  readonly heldRankId?: string | null;
  readonly rankById?: RankResolver | null;
}

/**
 * Is this id a never-awarded end-of-ladder marker? (Level 1.)
 *
 * @param rankId - A rank id (`ranksConfiguration` document id / `_id`)
 */
export function isSentinelRank(rankId: string | undefined | null): boolean {
  return !!rankId && SENTINEL_RANK_IDS.has(rankId);
}

/**
 * Does this rank definition opt into level 2 — «shown only to whoever holds it»?
 *
 * The flag is the payload's, and a missing flag means NO: before the new ranks
 * configuration is loaded the field arrives `undefined`, and that must read as "not
 * restricted" — the behaviour the product had before this rule existed.
 *
 * @param rank - A rank definition, or whatever the catalogue returned for the id
 */
export function isRestrictedRank(
  rank: RankVisibilityFields | undefined | null
): boolean {
  return rank?.hiddenInProgress === true;
}

/**
 * May this rank be shown to THIS viewer — carousel, tables, anywhere?
 *
 * The three levels, in order: a sentinel is never shown; a restricted rank is shown
 * only to its holder; anything else is shown.
 *
 * @param rankId - A rank id
 * @param viewer - Whose ladder this is; see {@link RankViewer}
 */
export function isVisibleRank(
  rankId: string | undefined | null,
  viewer: RankViewer
): boolean {
  if (!rankId) {
    return false;
  }
  // Level 1 — fails closed, and wins over everything below, including a viewer who
  // supposedly holds it and including the sentinel's own `hiddenInProgress`.
  if (isSentinelRank(rankId)) {
    return false;
  }
  // Level 3 — the common case: not flagged (or no catalogue to ask) ⇒ visible.
  if (!isRestrictedRank(viewer.rankById?.(rankId))) {
    return true;
  }
  // Level 2 — flagged: only the holder sees it.
  return viewer.heldRankId === rankId;
}

/**
 * The same map with everything this viewer must not see removed, insertion order
 * preserved.
 *
 * Takes and returns a `Map` keyed by rank id, so it works for both the `Rank`
 * catalogue and a user's `ranksCarrerData` without knowing either shape: the flag is
 * read through `viewer.rankById`, never off the map's own values.
 *
 * @param ranks - Ranks by id (undefined is tolerated: callers read it off a store)
 * @param viewer - Whose ladder this is; see {@link RankViewer}
 * @returns A NEW map holding only the ranks this viewer may see
 */
export function visibleRanks<T>(
  ranks: Map<string, T> | undefined | null,
  viewer: RankViewer
): Map<string, T> {
  const visible = new Map<string, T>();
  if (!ranks) {
    return visible;
  }
  ranks.forEach((rank, id) => {
    if (isVisibleRank(id, viewer)) {
      visible.set(id, rank);
    }
  });
  return visible;
}

/**
 * A "next rank" id, or `undefined` when there is no next rank WORTH SHOWING to this
 * viewer.
 *
 * Two ways to get `undefined` here, and both are the ladder ending: a COFOUNDER's
 * successor is the sentinel, and a BLACK-CROWN's successor is COFOUNDER — restricted,
 * and they are not the holder. Showing nothing is the correct answer for both; the
 * ladder is cut at BLACK-CROWN for everybody who is not a cofounder.
 *
 * @param nextRankId - The next-rank id as stored/computed upstream
 * @param viewer - Whose ladder this is; see {@link RankViewer}
 */
export function visibleNextRankId(
  nextRankId: string | undefined | null,
  viewer: RankViewer
): string | undefined {
  return isVisibleRank(nextRankId, viewer) ? (nextRankId as string) : undefined;
}

/**
 * Same as {@link visibleNextRankId}, for callers holding the whole object
 * (a `Rank`, a `RankStatus` — anything carrying `_id`).
 *
 * @param nextRank - The next-rank object as resolved upstream
 * @param viewer - Whose ladder this is; see {@link RankViewer}
 */
export function visibleNextRank<T extends { _id?: string }>(
  nextRank: T | undefined | null,
  viewer: RankViewer
): T | undefined {
  return nextRank && isVisibleRank(nextRank._id, viewer) ? nextRank : undefined;
}

/** The shape of one `ranksCarrerData` entry that {@link heldRankIdOf} needs. */
interface CarrerStatusLike {
  readonly _id?: string;
  readonly status?: string;
}

/**
 * The id of the rank the owner of a `ranksCarrerData` currently HOLDS — the input to
 * level 2 for callers that only have the carreer data (the ranks card renders other
 * people's ladders, so it cannot ask the session store).
 *
 * Tolerates both shapes on purpose: `ranksCarrerData` is typed as a `Map` across the
 * front but arrives from Firestore as a plain object, and the codebase reads it both
 * ways.
 *
 * @param carrerData - A user's `ranksCarrerData`, as a Map or as a plain object
 * @returns The rank id whose status is `"current"`, or `undefined`
 */
export function heldRankIdOf(
  carrerData:
    | Map<string, CarrerStatusLike>
    | Record<string, CarrerStatusLike>
    | undefined
    | null
): string | undefined {
  if (!carrerData) {
    return undefined;
  }
  const entries =
    carrerData instanceof Map
      ? Array.from(carrerData.entries())
      : Object.entries(carrerData);
  for (const [id, status] of entries) {
    if (status && status.status === "current") {
      return id;
    }
  }
  return undefined;
}
